import { useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import * as Sentry from '@sentry/browser';
import { PolarisActorContext } from 'src/contexts';

import type {
  InterviewFactoryOptions,
  InterviewSetting,
  MiniInterviewType,
} from 'src/packages/polaris/types';
import usePolaris from 'src/hooks/usePolaris';
import AppContext from 'src/contexts/AppContext';

type PolarisActorProviderProps = Partial<InterviewFactoryOptions> & {
  interviewType: MiniInterviewType;
  totalInterviews: number;
  isInitialInterview: boolean;
  parentInterviewId?: string;
  setIsPopUpOpen?: (isOpen: boolean) => void;
  isOpen?: boolean;
  interviewSettings?: InterviewSetting[];
};
function PolarisActorProvider(props: PropsWithChildren<PolarisActorProviderProps>) {
  const {
    children,
    id = '',
    totalInterviews,
    isInitialInterview,
    setIsPopUpOpen,
    isOpen,
    interviewSettings,
  } = props;

  const { send, state } = usePolaris();

  const { context } = state || {};
  const { currentInterview } = context || {};

  /**
   * This useEffect hook is used to log the state transitions of the state machine.
   * It only runs in the browser environment (not on server side rendering).
   */
  useEffect(() => {
    if (typeof window !== 'undefined' && state && currentInterview === id) {
      const message = `Transitioned to <${id}>${state.toStrings()}`;

      Sentry.addBreadcrumb({
        category: 'xstate',
        level: 'debug',
        message,
        type: 'info',
      });

      console.info(message);
    }
  }, [id, state, currentInterview]);

  useEffect(() => {
    if (context && !context.subActors[id]) {
      const options = {
        ...props,
      };
      send?.('CREATE_INTERVIEW', options);
    }
  });

  const isSingleInterview = totalInterviews === 1;
  const idInSubActors = context?.subActors && Object.keys(context.subActors).includes(id);
  const parentInterviewIsActive = currentInterview?.includes(props.parentInterviewId);

  const isVisible =
    isSingleInterview ||
    /*  If interview consists of several interview segments:
        - if parent interview is active, only render the current segment
        - if there is no active interview, only render the first segment */
    (parentInterviewIsActive ? currentInterview === id : isInitialInterview);
  /** Do not render until an actor has been created */
  if (!idInSubActors || !isVisible) {
    return null;
  }

  return (
    <PolarisActorContext.Provider value={context.subActors[id]}>
      <AppContext.Provider value={{ isOpen, setIsPopUpOpen, interviewSettings }}>
        {children}
      </AppContext.Provider>
    </PolarisActorContext.Provider>
  );
}

export default PolarisActorProvider;
